import { supabase } from '@/shared/supabase/client'
import type { Database } from '@/shared/supabase/database.types'
import { isNavigatorOnline } from '@/shared/lib/network'
import { logClientError } from '@/shared/lib/safeClientLog'
import { LOCAL_EMBEDDING_DIMENSION } from './embeddingEngine'
import {
  DEFAULT_LOCAL_AI_MEMORY_RETENTION_DAYS,
  localVectorStore,
  type LocalVectorStore,
} from './localVectorStore'
import { textLooksLikeBrazilianCpf } from './piiPatterns'
import { useLocalAIStore } from './useLocalAIStore'

export const SYNAP_AI_MEMORY_SYNC_QUEUE_KEY = 'synap-ai-memory-sync-queue'

export const MEMORY_SYNC_BATCH_SIZE = 10

export const MEMORY_SYNC_INTERVAL_MS = 5 * 60 * 1000

export interface PendingMemorySyncItem {
  localEntryId: string
  sessionId: string
  content: string
  role: 'user' | 'assistant'
  patientId?: string
  topic?: string
  createdAt: number
  isSummary?: boolean
}

const readQueue = (): PendingMemorySyncItem[] => {
  if (typeof localStorage === 'undefined') return []
  try {
    const raw = localStorage.getItem(SYNAP_AI_MEMORY_SYNC_QUEUE_KEY)
    if (!raw) return []
    const parsed = JSON.parse(raw) as unknown
    return Array.isArray(parsed) ? (parsed as PendingMemorySyncItem[]) : []
  } catch {
    return []
  }
}

function writeQueue(items: PendingMemorySyncItem[]): void {
  if (typeof localStorage === 'undefined') return
  try {
    if (items.length === 0) {
      localStorage.removeItem(SYNAP_AI_MEMORY_SYNC_QUEUE_KEY)
    } else {
      localStorage.setItem(SYNAP_AI_MEMORY_SYNC_QUEUE_KEY, JSON.stringify(items))
    }
  } catch {
    /* quota / private mode */
  }
}

/** Limpa a fila de sync (ex.: logout LGPD). */
export function clearMemorySyncQueue(): void {
  writeQueue([])
}

export function appendMemorySyncQueue(item: PendingMemorySyncItem): void {
  const next = [...readQueue(), item]
  writeQueue(next)
}

export function defaultShouldEnqueueMemorySync(): boolean {
  const { isLocalEnabled, isSyncEnabled } = useLocalAIStore.getState()
  return isLocalEnabled && isSyncEnabled
}

function syncConsentOk(): boolean {
  return defaultShouldEnqueueMemorySync()
}

let processing = false
let onlineHandler: (() => void) | null = null
let intervalId: number | null = null

export interface MemorySyncServiceDeps {
  vectorStore?: LocalVectorStore
}

const buildEmbeddingLiteral = (values: number[]): string => `[${values.join(',')}]`

/**
 * Upsert até {@link MEMORY_SYNC_BATCH_SIZE} itens da fila `localStorage` para `local_ai_memories`.
 * Respeita consentimento (IA local + sync na nuvem), sessão Supabase e estado online.
 */
export async function processMemorySyncQueueOnce(
  deps?: MemorySyncServiceDeps,
): Promise<{ processed: number; failed: number }> {
  if (typeof window === 'undefined') {
    return { processed: 0, failed: 0 }
  }
  if (!isNavigatorOnline() || !syncConsentOk()) {
    return { processed: 0, failed: 0 }
  }
  if (processing) {
    return { processed: 0, failed: 0 }
  }

  const { data: sessionData } = await supabase.auth.getSession()
  const userId = sessionData?.session?.user?.id
  if (!userId) {
    return { processed: 0, failed: 0 }
  }

  const fullQueue = readQueue()
  if (fullQueue.length === 0) {
    return { processed: 0, failed: 0 }
  }

  const store = deps?.vectorStore ?? localVectorStore
  const rawRetention = useLocalAIStore.getState().memoryRetentionDays
  const retentionDays =
    typeof rawRetention === 'number' && Number.isFinite(rawRetention)
      ? rawRetention
      : DEFAULT_LOCAL_AI_MEMORY_RETENTION_DAYS
  await store.open(retentionDays > 0 ? { retentionDays: retentionDays } : {})

  const batch = fullQueue.slice(0, MEMORY_SYNC_BATCH_SIZE)

  processing = true
  const removeIds = new Set<string>()
  let failed = 0
  const nowIso = new Date().toISOString()

  type LocalAiMemoryInsert = Database['public']['Tables']['local_ai_memories']['Insert']

  try {
    const rows: LocalAiMemoryInsert[] = []

    for (const item of batch) {
      const entry = await store.getById(item.localEntryId)
      if (!entry) {
        removeIds.add(item.localEntryId)
        continue
      }

      if (entry.embedding.length !== LOCAL_EMBEDDING_DIMENSION) {
        logClientError('[memorySync] dimensao de embedding invalida', new Error(item.localEntryId))
        removeIds.add(item.localEntryId)
        failed += 1
        continue
      }

      if (textLooksLikeBrazilianCpf(entry.content)) {
        logClientError(
          '[memorySync] entrada removida da fila — possivel documento (CPF) no texto; nao enviado ao servidor',
          new Error('pii-skip'),
        )
        removeIds.add(item.localEntryId)
        continue
      }

      const patientId = entry.metadata.patientId ?? item.patientId ?? null

      const { data: existing } = await supabase
        .from('local_ai_memories')
        .select('created_at')
        .eq('id', item.localEntryId)
        .maybeSingle()

      if (existing?.created_at) {
        const serverMs = new Date(existing.created_at as string).getTime()
        if (serverMs > entry.timestamp) {
          removeIds.add(item.localEntryId)
          continue
        }
      }

      rows.push({
        id: item.localEntryId,
        user_id: userId,
        session_id: item.sessionId,
        content: entry.content,
        embedding: buildEmbeddingLiteral(entry.embedding),
        role: entry.role,
        patient_id: patientId,
        is_summary: entry.metadata.summary ?? item.isSummary ?? false,
        created_at: new Date(entry.timestamp).toISOString(),
        synced_at: nowIso,
      })
    }

    if (rows.length > 0) {
      const { error } = await supabase.from('local_ai_memories').upsert(rows, {
        onConflict: 'id',
      })
      if (error) {
        logClientError('[memorySync] upsert falhou', error)
        failed += 1
      } else {
        const syncedMs = Date.now()
        const ids = rows.map((r) => r.id as string)
        await store.markSynced(ids, syncedMs)
        for (const id of ids) {
          removeIds.add(id)
        }
      }
    }

    const nextQueue = fullQueue.filter((q) => !removeIds.has(q.localEntryId))
    writeQueue(nextQueue)

    return { processed: removeIds.size, failed }
  } finally {
    processing = false
  }
}

export function installMemorySyncLifecycle(): void {
  if (typeof window === 'undefined') return
  if (onlineHandler) return

  onlineHandler = () => {
    void processMemorySyncQueueOnce()
  }
  window.addEventListener('online', onlineHandler)

  intervalId = window.setInterval(() => {
    if (isNavigatorOnline()) {
      void processMemorySyncQueueOnce()
    }
  }, MEMORY_SYNC_INTERVAL_MS)

  void processMemorySyncQueueOnce()
}

export function uninstallMemorySyncLifecycle(): void {
  if (typeof window === 'undefined') return
  if (onlineHandler) {
    window.removeEventListener('online', onlineHandler)
    onlineHandler = null
  }
  if (intervalId !== null) {
    window.clearInterval(intervalId)
    intervalId = null
  }
}
